<!--
	@name app-air-academy-dashboard
	@description Join tab view for the air academy page
	@date 2020/02/21
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<v-dialog v-model="active" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable>
			<v-card tile>
				<v-toolbar flat dark class="pl-3 pr-3 flex-grow-0 generic-gradient">
					<v-row>
						<v-col cols="12" sm="10" class="d-flex pa-0">
							<v-toolbar-title class="align-self-center">
								{{ title }}
							</v-toolbar-title>
						</v-col>
						<v-spacer />
						<v-col cols="12" sm="2" class="d-flex pa-0 justify-end">
							<v-btn icon dark class="align-self-center" @click="active = false">
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</v-col>
					</v-row>
				</v-toolbar>
				<v-card-text>
					<v-container class="px-0 pb-10">
						<v-sheet class="pa-6 mb-3 rounded grey lighten-2">
							<v-row no-gutters>
								<v-col cols="12" sm="7" class="align-content-center">
									<p class="text-h6 mb-0">
										{{ title }}
									</p>
								</v-col>
								<v-col cols="12" sm="5" class="grow text-right">
									<slot name="logo" />
								</v-col>
							</v-row>
						</v-sheet>
						<slot name="highlight" />

						<v-row class="mb-6">
							<v-col cols="7" class="pr-10">
								<slot name="copy" />
							</v-col>
							<v-col cols="5">
								<div v-if="vimeo && Object.keys(vimeo).length">
									<v-skeleton-loader v-if="!testimonial.name" type="card" class="pa-0 ma-0" />
									<app-air-academy-training-webinar-item v-else :item="testimonial" @play-vimeo="playVimeo" />
									<app-dialog-vimeo-player ref="vimeoDialog" />
								</div>
								<div v-else>
									<v-skeleton-loader v-if="!testimonial.name" type="card" class="pa-0 ma-0" />
									<app-air-academy-training-webinar-item v-else :item="testimonial" @play-video="playVideo" />
									<app-dialog-video-player ref="videoDialog" />
								</div>
							</v-col>
						</v-row>

						<v-divider class="my-8" inset />

						<div v-if="authStatus == 'error'" class="mt-6">
							<p class="font-weight-bold body-1 mb-4">
								To link your Air Academy account, please click the button below. If you have any questions, please <a href="mailto:Academy.enquiries@airlaterlife.co.uk">click here</a> to get in contact.
							</p>

							<v-btn light color="primary" @click="openAuthoriseDialog">
								Connect my Air Academy Learning Platform
							</v-btn>
						</div>

						<div v-else-if="!catalog.length" class="mt-8 text-center">
							<p class="mb-2 body-1 font-italic">
								Retrieving LMS course list
							</p>
							<v-progress-linear height="10" indeterminate />
						</div>

						<div v-else-if="authStatus == null" class="mt-8 text-center">
							<p class="mb-2 body-1 font-italic">
								Retrieving your LMS account details
							</p>
							<v-progress-linear height="10" indeterminate />
						</div>

						<div v-else-if="filteredCoursesToDisplay.length > 0">
							<v-select
								v-if="categories.length"
								v-model="selectedCategory"
								:items="categories"
								label="Filter by category"
								placeholder="Filter by category"
								class="mb-4"
								dense
								hide-details
								outlined
								solo
								clearable
								style="max-width: 380px;"
							/>

							<v-row>
								<v-col v-for="(item, index) in filteredCoursesToDisplay" :key="item.id" cols="4">
									<app-air-academy-training-webinar-item :key="index" :item="item" />
								</v-col>
							</v-row>
						</div>

						<template v-else>
							<v-alert type="error" class="text-body-2 mb-3">
								Error retrieving LMS course list
							</v-alert>
						</template>
					</v-container>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
	import AppAirAcademyTrainingWebinarItem from '@/component/app/air-academy/training/webinar-item';
	import AppDialogVideoPlayer from '@/component/app/dialog/video-player';
	import AppDialogVimeoPlayer from '@/component/app/dialog/vimeo-player';
	import { EventBus } from '@/utils';
	import { orderBy } from 'lodash';
	import { mapGetters, mapState } from 'vuex';

	export default {
		name: 'app-air-academy-dashboard',

		components: {
			AppAirAcademyTrainingWebinarItem,
            AppDialogVideoPlayer,
			AppDialogVimeoPlayer
		},

        props: {
			restricted: { type: Boolean, required: false, default: true },
			ring: { type: Number, required: true },
			catalogId: { type: Number, required: true },
			title: { type: String, required: true },
			video: { type: String, required: false, default: null },
			vimeo: { type: Object, required: false, default: () => {} },
			testimonial: { type: Object, default: () => {} },
			lenders: { type: Array, default: () => [] }
		},

		data() {
			return {
				active: false,
				selectedCategory: null,
                categoryMapping: {
					'Air Systems Support':[241, 240, 239, 238, 237, 236, 235, 234, 232, 231, 230],
					'On Demand Learning': [332, 416, 310, 309, 308, 278, 256, 255, 248, 246, 244, 243, 242],
					'Live Event Recordings': [396, 395, 394, 393, 392, 391, 397, 398, 399, 400, 401, 402, 403, 386, 385, 384, 383, 382, 381, 380, 379, 378, 329, 328, 327, 326, 325],
					'Podcasts': [390, 389, 388, 387, 387, 377, 376, 375, 374, 373, 372, 371, 370, 369, 368, 367, 366, 365, 364, 363, 362, 333, 331, 330],
					'Marketing Support': [305, 306, 256, 255, 248, 246, 244, 243, 242]
				}
			};
		},

		computed: {
			...mapState('AppWebinar', ['authStatus', 'catalogs']),
			...mapGetters('AppWebinar', ['getCatalogWithEnrolment', 'getRingSortOption', 'getRingMot']),

			/**
			 * @name catalog
			 * @description Fetch all courses in a given catalog and merge enrolment data
			 * @return {Array}
			 */
			catalog() {
				return this.getCatalogWithEnrolment(this.ring, this.catalogId);
			},

			/**
			 * @name mot
			 * @description TODO
			 * @return {Array}
			 */
			mot() {
				return this.getRingMot(this.ring, this.catalogId);
			},

			/**
			 * @name sortFunction
			 * @description TODO
			 * @return {Array}
			 */
			sortFunction() {
				return this.getRingSortOption(this.ring);
			},

			categories() {
				return Object.keys(this.categoryMapping).filter(category => {
					const categoryIds = this.categoryMapping[category];
					return this.coursesToDisplay.some(course => categoryIds.includes(Number(course.id_course)));
				});
			},

			filteredCoursesToDisplay() {
				if (!this.selectedCategory) {
					return this.coursesToDisplay;
				}
				const categoryIds = this.categoryMapping[this.selectedCategory];
				return this.coursesToDisplay.filter(course => categoryIds.includes(Number(course.id_course)));
			},

			/**
			 * @name coursesToDisplay
			 * @description TODO
			 * @return {Array}
			 */
			coursesToDisplay() {
				//if no restrction in place, just show entire category
                if (!this.restricted) {
					//do we have custom sorting options
					if (this.sortFunction) {
						return orderBy(
							this.catalog,
							this.sortFunction.fnctn,
							this.sortFunction.order
						);
					}

					return this.catalog;
				}

				let courses = this.catalog;

				// are we showing the MOT?
				if (this.mot.length) courses = [...courses, ...this.mot];

				//do we have custom sorting options
				if (this.sortFunction) {
					return orderBy(
						courses,
						this.sortFunction.fnctn,
						this.sortFunction.order
					);
				}

				return courses;
			}
		},

		methods: {
            open() {
                this.active = true;
            },

            close() {
                this.active = false;
            },

			playVideo() {
				this.$refs.videoDialog.open(`${process.env.VUE_APP_S3_AIR_ASSETS_URL}/${this.video}`);
			},

			playVimeo() {
				this.$refs.vimeoDialog.open(this.vimeo);
			},

			openAuthoriseDialog() {
				EventBus.$emit('open-authorise-academy-dialog');
			}
		}
	};
</script>

<style scoped lang="scss">
</style>